import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CAMPAIGN_LIST_DEFAULT_COLUMN_ORDER,
  CAMPAIGN_LIST_DEFAULT_WIDTHS,
  CAMPAIGN_LIST_REQUIRED_COLUMNS,
  clampCampaignColumnWidth,
  campaignBalancedFillWidth,
  normalizeCampaignColumnOrder,
  reorderCampaignColumns,
  type CampaignListDataColumnId,
} from "./campaign-list-table.js";

export function useCampaignTableLayout(canSelect: boolean): {
  columnOrder: CampaignListDataColumnId[];
  visibleColumns: CampaignListDataColumnId[];
  hiddenColumns: Set<CampaignListDataColumnId>;
  widths: Record<CampaignListDataColumnId, number>;
  setContainerWidth: (width: number) => void;
  toggleColumn: (id: CampaignListDataColumnId) => void;
  moveColumn: (id: CampaignListDataColumnId, direction: "up" | "down") => void;
  reorderColumn: (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => void;
  resizeColumn: (id: CampaignListDataColumnId, width: number) => void;
  resetColumns: () => void;
} {
  const [columnOrder, setColumnOrder] = useState<CampaignListDataColumnId[]>(
    CAMPAIGN_LIST_DEFAULT_COLUMN_ORDER,
  );
  const [hiddenColumns, setHiddenColumns] = useState<Set<CampaignListDataColumnId>>(
    () => new Set(),
  );
  const [widths, setWidths] = useState<Record<CampaignListDataColumnId, number>>(
    CAMPAIGN_LIST_DEFAULT_WIDTHS,
  );
  const [userSized, setUserSized] = useState<Set<CampaignListDataColumnId>>(() => new Set());
  const [containerWidth, setContainerWidth] = useState(0);

  const visibleColumns = useMemo(
    () => columnOrder.filter((id) => !hiddenColumns.has(id)),
    [columnOrder, hiddenColumns],
  );

  const filledWidths = useMemo(
    () =>
      campaignBalancedFillWidth({
        containerWidth,
        canSelect,
        visible: visibleColumns,
        widths,
        userSized,
      }),
    [canSelect, containerWidth, userSized, visibleColumns, widths],
  );

  const toggleColumn = useCallback((id: CampaignListDataColumnId) => {
    if (CAMPAIGN_LIST_REQUIRED_COLUMNS.has(id)) return;
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      const remaining = columnOrder.filter((colId) => colId !== id && !next.has(colId)).length;
      if (remaining === 0) return prev;
      next.add(id);
      return next;
    });
  }, [columnOrder]);

  const moveColumn = useCallback((id: CampaignListDataColumnId, direction: "up" | "down") => {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id);
      if (index < 0) return prev;
      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(index, 1);
      next.splice(target, 0, item!);
      return next;
    });
  }, []);

  const reorderColumn = useCallback(
    (fromId: CampaignListDataColumnId, toId: CampaignListDataColumnId) => {
      setColumnOrder((prev) => reorderCampaignColumns(prev, fromId, toId));
    },
    [],
  );

  const resizeColumn = useCallback((id: CampaignListDataColumnId, width: number) => {
    setUserSized((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setWidths((prev) => ({ ...prev, [id]: clampCampaignColumnWidth(id, width) }));
  }, []);

  const resetColumns = useCallback(() => {
    setColumnOrder(CAMPAIGN_LIST_DEFAULT_COLUMN_ORDER);
    setHiddenColumns(new Set());
    setWidths(CAMPAIGN_LIST_DEFAULT_WIDTHS);
    setUserSized(new Set());
  }, []);

  useEffect(() => {
    setColumnOrder((prev) => normalizeCampaignColumnOrder(prev));
  }, []);

  return {
    columnOrder,
    visibleColumns,
    hiddenColumns,
    widths: filledWidths,
    setContainerWidth,
    toggleColumn,
    moveColumn,
    reorderColumn,
    resizeColumn,
    resetColumns,
  };
}
